/* V162.2 — LaunchCountdownWidget — D:H:M:S countdown for agent launch.

   Renders on /agent/<slug> when admin has set launch_at via Roadmap.
   3 states:
   - countdown_active → live ticker + "Reserve my seat now" CTA
   - launched         → 🎉 confetti banner + "Activate now" CTA
   - not_scheduled    → hidden (no render)

   Polls /api/agents/launch-info every 30s for fresh seat counts.
   Local seconds-tick every 1s for smooth UX.
*/
import { useEffect, useState } from "react";
import axios from "axios";
import { Clock, Sparkles, Rocket } from "lucide-react";
import { API } from "../../config";

export default function LaunchCountdownWidget({ agentSlug, onReserveClick }) {
  const [info, setInfo] = useState(null);
  const [tick, setTick] = useState(0);

  // Fetch fresh info every 30s
  useEffect(() => {
    if (!agentSlug) return;
    let cancelled = false;
    const load = () =>
      axios
        .get(`${API}/agents/launch-info?slug=${encodeURIComponent(agentSlug)}`)
        .then((r) => { if (!cancelled) setInfo(r.data); })
        .catch(() => {});
    load();
    const id = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [agentSlug]);

  // Local 1s tick for smooth countdown UI (decouples from polling)
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  if (!info) return null;
  if (info.countdown_status === "not_scheduled") return null;

  // === Launched state ===
  if (info.countdown_status === "launched") {
    return (
      <div
        className="border-2 border-emerald-500 bg-gradient-to-br from-emerald-950/60 to-zinc-950 p-5"
        data-testid="launch-countdown-launched"
      >
        <div className="flex items-center gap-3 mb-3">
          <Rocket className="w-7 h-7 text-emerald-400" />
          <div>
            <div className="font-black text-xl text-white">
              🎉 {info.emoji} {info.name} je LIVE!
            </div>
            <div className="text-xs text-emerald-300">
              Spustený · môžeš ho aktivovať okamžite
            </div>
          </div>
        </div>
        <a
          href={`/agent-os/demo/${info.slug}`}
          className="block w-full text-center bg-emerald-500 hover:bg-emerald-400 text-black font-black py-3 uppercase tracking-widest text-sm"
          data-testid="launch-activate-btn"
        >
          🚀 Aktivovať teraz →
        </a>
      </div>
    );
  }

  // === Countdown active ===
  const c = info.countdown || {};
  const seatsLeft = info.early_bird_seats_remaining || 0;
  const seatsTotal = info.early_bird_total || 100;
  const urgent = (c.days ?? 99) < 1; // <1 day → red accent

  // Pull live-recompute from total_seconds + elapsed local ticks since last poll
  const totalSec = Math.max(0, (c.total_seconds || 0) - tick);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = Math.floor(totalSec % 60);

  const accent = urgent ? "#EF4444" : "#F59E0B";

  return (
    <div
      className="border-2 p-5 bg-gradient-to-br from-zinc-950 via-purple-950/30 to-zinc-950"
      style={{ borderColor: accent }}
      data-testid="launch-countdown-active"
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div
          className="w-11 h-11 flex items-center justify-center flex-shrink-0"
          style={{ background: `${accent}22` }}
        >
          <Clock className="w-5 h-5" style={{ color: accent }} />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest font-black mb-0.5" style={{ color: accent }}>
            {urgent ? "🚨 Posledný deň pred launchom" : "⏰ Launch countdown"}
          </div>
          <div className="font-black text-base text-white">
            {info.emoji} {info.name}
          </div>
          <div className="text-xs text-zinc-400 mt-0.5">
            {info.votes_count} 🔥 hlasov · €{info.price_eur_month}/mo (lifetime 50% off: €{Math.round(info.price_eur_month * 0.5 * 100) / 100}/mo)
          </div>
        </div>
      </div>

      {/* D:H:M:S ticker */}
      <div className="grid grid-cols-4 gap-1.5 mb-4" data-testid="launch-countdown-ticker">
        {[
          { label: "DNI", value: days },
          { label: "HOD", value: hours },
          { label: "MIN", value: minutes },
          { label: "SEK", value: seconds },
        ].map((unit) => (
          <div key={unit.label} className="bg-zinc-900 border border-zinc-800 py-3 text-center">
            <div className="text-2xl md:text-3xl font-black text-white font-mono tabular-nums">
              {String(unit.value).padStart(2, "0")}
            </div>
            <div className="text-[9px] uppercase tracking-widest font-black text-zinc-500 mt-0.5">
              {unit.label}
            </div>
          </div>
        ))}
      </div>

      {/* Seat scarcity bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-black mb-1.5">
          <span className="text-zinc-500 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Lifetime miest
          </span>
          <span style={{ color: accent }} data-testid="launch-seats-left">
            {seatsLeft} / {seatsTotal} zostáva
          </span>
        </div>
        <div className="h-1.5 bg-zinc-800 overflow-hidden">
          <div
            className="h-full transition-all duration-500"
            style={{
              width: `${Math.min(100, ((seatsTotal - seatsLeft) / seatsTotal) * 100)}%`,
              background: accent,
            }}
          />
        </div>
      </div>

      {/* CTA */}
      <button
        onClick={onReserveClick}
        className="w-full py-3 font-black text-sm uppercase tracking-widest text-black hover:opacity-90 transition-opacity"
        style={{ background: accent }}
        data-testid="launch-reserve-btn"
      >
        🔒 Rezervujem si miesto teraz
      </button>

      <p className="text-[10px] text-zinc-500 mt-2 text-center">
        Hlasovanie = rezervácia. Po launch dostaneš email s aktivačným linkom.
      </p>
    </div>
  );
}
